import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Palette, Users, Phone, ArrowRight, Star, BookOpen } from 'lucide-react';
import { detectRadioSource, storeRadioSource, getUTMParams } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

/**
 * Radio Landing Page - /welcome
 * 
 * KPAC Radio Landing Page
 * ========================
 * This is the ONLY radio landing page. KPAC listeners are directed here via:
 *   sanantonioartschool.org → 301 redirect → thecoppini.org/welcome
 * 
 * KAHL Radio does NOT use this page - they go directly to thecoppini.org
 * 
 * Tracking:
 * - Capture any UTM parameters for analytics
 * - Store "kpac_radio" source in session for attribution
 * - Remove UTMs from URL after capture (keep URL clean)
 * - Attribution persists through contact form submission
 */
export default function RadioWelcome() {
  useEffect(() => {
    // Check for UTM parameters (may be present in redirect)
    const utmParams = getUTMParams();
    const hasUTM = utmParams.utm_source || utmParams.utm_medium || utmParams.utm_campaign;
    
    // Detect specific source from UTM if present
    const detectedSource = detectRadioSource();
    
    // Default to kpac_radio since this page is specifically for KPAC
    // (sanantonioartschool.org redirects here)
    const radioSource = detectedSource || 'kpac_radio';
    
    // Track the radio landing — direct gtag call, once per page load
    if (window.location.pathname.includes('/welcome') && window.gtag) {
      gtag('event', 'radio_landing', {
        event_category: 'radio_attribution',
        event_label: 'kpac_radio',
        radio_source: 'kpac_radio',
        landing_page: window.location.pathname
      });
    }
    
    // Store for later attribution (contact form, registrations)
    storeRadioSource(radioSource);
    
    // Clean up URL by removing UTM params (keeps URL clean for sharing)
    if (window.location.search) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Welcome"
        description="Welcome to The Coppini Academy of Fine Art in San Antonio. Explore painting, drawing, and sculpture classes for adults and youth."
        canonicalPath="/welcome"
      />
      <section 
        className="relative py-24 md:py-32"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_coppini-art/artifacts/r57gtupb_resharpen_studio.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/60" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Star size={16} fill="currentColor" />
            Welcome, Friend!
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight drop-shadow-lg">
            You're in the<br />Right Place
          </h1>
          
          <p className="text-xl sm:text-2xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            Thank you for your interest in <strong>The Coppini Academy of Fine Art</strong> — 
            San Antonio's home for classical art education since 1945.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/classes/adult">
              <Button size="lg" className="w-full sm:w-auto bg-cafa-red hover:bg-cafa-red-900 text-lg px-8 py-6">
                Explore Adult Classes
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/classes/kids">
              <Button size="lg" className="w-full sm:w-auto bg-white text-cafa-red hover:bg-stone-100 text-lg px-8 py-6">
                Kids Classes
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-4">
            <Link to="/about/history">
              <Button size="lg" variant="ghost" className="text-white/90 hover:text-white hover:bg-white/10 text-lg">
                Our History →
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="ghost" className="text-white/90 hover:text-white hover:bg-white/10 text-lg">
                <Phone className="mr-2" size={18} />
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Who We Are - Simple Explanation */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif text-stone-900 mb-4">Who We Are</h2>
            <p className="text-lg text-stone-600 max-w-3xl mx-auto">
              The Coppini Academy is a nonprofit art school in the heart of San Antonio, 
              dedicated to teaching traditional, representational art to students of all ages and skill levels.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-stone-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Palette className="text-blue-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-stone-900 mb-2">Adult Classes</h3>
                <p className="text-stone-600 mb-4">
                  Drawing, painting, and more — from complete beginners to advanced artists.
                </p>
                <Link to="/classes/adult" className="text-cafa-red hover:text-cafa-red-900 font-medium inline-flex items-center">
                  View Classes <ArrowRight size={16} className="ml-1" />
                </Link>
              </CardContent>
            </Card>

            <Card className="border-stone-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-green-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-stone-900 mb-2">Kids Classes</h3>
                <p className="text-stone-600 mb-4">
                  Coppini Kids classes for ages 5–18, nurturing the next generation of artists.
                </p>
                <Link to="/classes/kids" className="text-cafa-red hover:text-cafa-red-900 font-medium inline-flex items-center">
                  Kids Programs <ArrowRight size={16} className="ml-1" />
                </Link>
              </CardContent>
            </Card>

            <Card className="border-stone-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="text-amber-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-stone-900 mb-2">Our History</h3>
                <p className="text-stone-600 mb-4">
                  Founded in 1945 — learn about our rich legacy and the artists who shaped us.
                </p>
                <Link to="/about/history" className="text-cafa-red hover:text-cafa-red-900 font-medium inline-flex items-center">
                  Read Our Story <ArrowRight size={16} className="ml-1" />
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Location & Hours - Reassurance */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 md:p-10">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-serif text-stone-900 mb-4">Visit Us</h3>
                <div className="space-y-3 text-lg text-stone-600">
                  <p className="font-medium text-stone-900">
                    115 Melrose Place<br />
                    San Antonio, TX 78212
                  </p>
                  <p className="text-stone-500 italic">
                    (Right behind Don & Ben's Liquor — look for the historic studio!)
                  </p>
                  <p className="pt-2">
                    <strong>Phone:</strong>{' '}
                    <a href="tel:210-530-8445" className="text-cafa-red hover:underline">
                      (210) 530-8445
                    </a>
                  </p>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-serif text-stone-900 mb-4">Hours</h3>
                <div className="space-y-2 text-lg text-stone-600">
                  <p><strong>Mon–Fri:</strong> 9 AM – 9:30 PM</p>
                  <p><strong>Saturday:</strong> 10 AM – 4 PM</p>
                  <p><strong>Sunday:</strong> 3 PM – 5 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA - Soft lead to contact form */}
      <section className="py-16 bg-cafa-red">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif text-white mb-4">
            Have Questions?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            We'd love to hear from you. Whether you're curious about classes, workshops, 
            or just want to stop by — reach out anytime.
          </p>
          <Link to="/contact">
            <Button size="lg" variant="secondary" className="text-lg px-8 py-6 bg-white text-cafa-red hover:bg-stone-100">
              <Phone className="mr-2" size={20} />
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-stone-500 text-lg">
            Founded in 1945 • Nonprofit 501(c)(3) • Over 75 Years of Art Education in San Antonio
          </p>
        </div>
      </section>
    </div>
  );
}
